import { Company } from "../../../../types/companies/Companies";

export type PatientId = number | string;

export type SlaTone = 'success' | 'warning' | 'error' | 'info';
export type KpiTone = 'green' | 'blue' | 'orange' | 'purple';
export type PatientDetailsMode = 'view' | 'create' | 'review';
export type PatientV2Profile = 'solicitante' | 'avaliador' | 'avaliador_regulatorio';
export type PatientV2GeneralStatus = 'Em preenchimento' | 'Em análise' | 'Pendente' | 'Ativo' | 'Deferido provisório' | 'Indeferimento provisório' | 'Indeferido';
export type PatientV2SectionStatus = 'Pendente' | 'Aprovado';
export type PatientV2DocumentStatus = 'Aguardando avaliação' | 'Pendente' | 'Aprovado';
export type ReviewDocumentStatus = 'pending' | 'validated' | 'invalid';
export type ReviewSectionKey = 'patient' | 'clinical' | 'documents';
export type ReviewSectionStatus = 'pending' | 'confirmed';

export const PATIENT_V2_PROFILE_OPTIONS: Array<{ value: PatientV2Profile; label: string }> = [
  { value: 'solicitante', label: 'Solicitante' },
  { value: 'avaliador', label: 'Avaliador técnico' },
  { value: 'avaliador_regulatorio', label: 'Avaliador regulatório' },
];

export const normalizePatientV2Profile = (value: unknown, isAdmin?: unknown): PatientV2Profile => {
  if (value === 'avaliador' || value === 'avaliador_regulatorio' || value === 'solicitante') return value;
  return isAdmin === '1' || isAdmin === true ? 'avaliador_regulatorio' : 'solicitante';
};

export const canReviewPatientsV2 = (profile: unknown): boolean => profile === 'avaliador' || profile === 'avaliador_regulatorio';

export const canAccessPatientsV2 = (
  user:
    | {
        isAdmin?: unknown;
        accessPremium?: unknown;
      }
    | null
    | undefined
): boolean => {
  const isAdmin = user?.isAdmin === true || user?.isAdmin === '1';
  const hasPremiumAccess = user?.accessPremium === true || user?.accessPremium === '1';
  return isAdmin || hasPremiumAccess;
};

export const canMakeRegulatoryDecisionV2 = (profile: unknown): boolean => profile === 'avaliador_regulatorio';

export interface PatientV2Kpi {
  label: string;
  value: string;
  detail: string;
  tone: KpiTone;
}

export interface PatientV2Row {
  id: PatientId;
  patient: string;
  cns: string;
  urs: string;
  crs: string;
  requestedAt: string;
  sla: string;
  slaTone: SlaTone;
  regulatoryStatus: string;
}

export interface PatientV2ClinicalData {
  cidPrimary: string;
  cidSecondary: string;
  diagnosis: string;
  oxygenIndication: string;
  oxygenSource: string;
  oxygenFlow: string;
  dailyUse: string;
  requestedEquipment: string;
  requestingDoctorName: string;
  requestingDoctorCrm: string;
  requestDate: string;
  reviewStatus?: string;
  reviewNotes?: string;

  requestingDoctorCrmNumber?: string;
  requestingDoctorCrmUf?: string;
}

export interface PatientV2Document {
  id: number;
  name: string;
  type: string;
  documentGeneratedAt?: string;
  documentExpiresAt?: string;
  size: string;
  status: string;
  tone: SlaTone;
  reviewStatus?: ReviewDocumentStatus;
  reviewReason?: string;
  uploadedAt?: string;
}

export interface PatientV2ReviewIssue {
  section: string;
  field: string;
  description: string;
  label?: string;
  documentId?: PatientId;
}

export interface PatientV2ReviewData {
  sectionStatus: Partial<Record<ReviewSectionKey, ReviewSectionStatus>>;
  issues: PatientV2ReviewIssue[];
  documents: PatientV2Document[];
}

export interface PatientV2ApprovalSections {
  patient: string;
  clinical: string;
  documents?: string;
}

export interface PatientV2RegulatoryDecisionData {
  id: PatientId;
  decision: string;
  justification?: string | null;
  decidedBy?: PatientId;
  decidedByName?: string;
  decidedAt?: string;
  createdAt?: string;
  isCurrent?: boolean;
}

export interface PatientV2HistoryItem {
  id?: PatientId;
  eventType?: string;
  regulatoryStatus?: string;
  decision?: string | null;
  reason?: string | null;
  createdAt?: string;
  /** Campos de compatibilidade para mocks antigos, sem contrato de API. */
  title?: string;
  date?: string;
  description?: string;
}

export interface PatientV2Detail extends PatientV2Row {
  id: PatientId;
  sap: string;
  cpf: string;
  birthDate: string;
  email: string;
  cellphone: string;
  phoneNumber: string;
  phones: string;
  cep: string;
  city: string;
  uf: string;
  address: string;
  number: string;
  neighborhood: string;
  addressLine2: string;
  companyId: number;
  company?: Company;
  canEdit?: boolean;
  canStartReview?: boolean;
  sts: string;
  doctor: string;
  clinical: PatientV2ClinicalData;
  documents: PatientV2Document[];
  history: PatientV2HistoryItem[];
  protocol: string;
  review?: PatientV2ReviewData;
  approvalSections?: PatientV2ApprovalSections;
  regulatoryDecision?: PatientV2RegulatoryDecisionData | null;
  operationalStatus?: string;
  canMakeRegulatoryDecision?: boolean;
}

export interface PatientV2FilterOptions {
  urs: string[];
  crs: string[];
  regulatoryStatus: string[];
}

export type PatientDetailsPatch = Omit<Partial<PatientV2Detail>, 'clinical'> & {
  clinical?: Partial<PatientV2ClinicalData>;
};

export interface PatientUploadFile {
  fileName: string;
  type: string;
  size: number;
}

export interface PatientUploadPayload {
  files: PatientUploadFile[];
}
